import React, { useCallback } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import Radio from "antd/es/radio";
import Space from "antd/es/space";
import { FieldData } from "rc-field-form/lib/interface";
import { useIntl } from "react-intl";

import MileageTrackingVisibility from "@mapmycustomers/shared/enum/mileageTracking/MileageTrackingVisibility";
import Organization from "@mapmycustomers/shared/types/Organization";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import CurrencyInput from "@app/component/input/CurrencyInput";
import RadioGroup from "@app/component/input/RadioGroup";
import localSettings from "@app/config/LocalSettings";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import MileageTrackingSettings from "@app/scene/settings/type/MileageTrackingSettings";
import showSaveSettingSuccessfulMessage from "@app/scene/settings/util/showSaveSettingSuccessfulMessage";
import {
  getOrganizationId,
  getOrganizationSettings,
  isMileageTrackingVisible,
} from "@app/store/iam";
import { updateOrganization } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useSetting from "@app/util/hook/useSetting";

import styles from "./MileageTracking.module.scss";
import Upsell from "./Upsell";

interface Props {
  form: FormInstance<MileageTrackingSettings>;
  isMileageTrackingVisible: boolean;
  onHasChanges: () => void;
  onSaved: () => void;
  onUpdateOrganization: typeof updateOrganization.request;
  organizationId?: number;
  organizationSettings: Organization["settings"];
}

const MileageTracking: React.FC<Props> = ({
  form,
  isMileageTrackingVisible,
  onHasChanges,
  onSaved,
  onUpdateOrganization,
  organizationId,
  organizationSettings,
}) => {
  const analytics = useExtendedAnalytics("Mileage Tracker tab", useAnalytics());

  const intl = useIntl();

  const rateSetting = useSetting(organizationSettings, OrganizationSetting.MILEAGE_TRACKING_RATE);
  const visibilitySetting = useSetting(
    organizationSettings,
    OrganizationSetting.MILEAGE_TRACKING_VISIBILITY
  );

  const handleSubmit = useCallback(() => {
    const { rate, visibility } = form.getFieldsValue();
    if (organizationId) {
      onUpdateOrganization({
        callback: () => {
          showSaveSettingSuccessfulMessage(intl);
          onSaved();
        },
        organization: { id: organizationId },
        settings: [
          rateSetting && rate > 0 ? { ...rateSetting, value: rate } : undefined,
          visibilitySetting && visibility ? { ...visibilitySetting, value: visibility } : undefined,
        ].filter(isDefined),
      });
    }
  }, [rateSetting, visibilitySetting, form, intl, organizationId, onUpdateOrganization, onSaved]);

  const siUnits = localSettings.doesUseSiUnits();

  const handleFieldsChange = useCallback(
    (changedFields: FieldData[]) => {
      changedFields.forEach((fieldData) => {
        switch ((fieldData.name as string[])[0]) {
          case "visibility":
            analytics.using(["Change View Permissions"], { value: fieldData.value });
            break;
          case "rate":
            analytics.using(["Change Reimbursement Amount"], { value: fieldData.value });
            break;
        }
      });
      onHasChanges();
    },
    [analytics, onHasChanges]
  );

  return isMileageTrackingVisible ? (
    <Form
      form={form}
      initialValues={{
        rate: rateSetting.value ?? 0,
        visibility: visibilitySetting.value ?? MileageTrackingVisibility.ALL,
      }}
      onFieldsChange={handleFieldsChange}
      onFinish={handleSubmit}
    >
      <div>
        {intl.formatMessage(
          {
            id: "settings.organizationDetails.mileageTracking.rate",
            defaultMessage: "Reimbursement amount per {unit, select, true {km} other {mile}}",
            description: "Settings organization details - MileageTracking - Rate label",
          },
          { unit: siUnits }
        )}
      </div>
      <Form.Item name="rate">
        <CurrencyInput
          inputClassName={styles.rateField}
          // We're ok losing some precision here
          // eslint-disable-next-line no-loss-of-precision
          max={99999999999999.99}
          min={0}
          step={0.1}
        />
      </Form.Item>

      <div>
        {intl.formatMessage({
          id: "settings.organizationDetails.mileageTracking.visibility",
          defaultMessage: "Show reimbursement value of trips to",
          description: "Settings organization details - MileageTracking - Visibility label",
        })}
      </div>
      <Form.Item name="visibility">
        <RadioGroup size="small">
          <Space className={styles.space} direction="vertical">
            <Radio value={MileageTrackingVisibility.NONE}>
              {intl.formatMessage({
                id: "settings.organizationDetails.mileageTracking.visibility.none",
                defaultMessage: "No one",
                description:
                  "Settings organization details - MileageTracking - Visibility - Nobody",
              })}
            </Radio>
            <Radio value={MileageTrackingVisibility.OWNER}>
              {intl.formatMessage({
                id: "settings.organizationDetails.mileageTracking.visibility.owner",
                defaultMessage: "Organization owners only",
                description:
                  "Settings organization details - MileageTracking - Visibility - Owners",
              })}
            </Radio>
            <Radio value={MileageTrackingVisibility.TEAM}>
              {intl.formatMessage({
                id: "settings.organizationDetails.mileageTracking.visibility.team",
                defaultMessage: "Team managers and organization owners",
                description:
                  "Settings organization details - MileageTracking - Visibility - Owners and managers",
              })}
            </Radio>
            <Radio value={MileageTrackingVisibility.ALL}>
              {intl.formatMessage({
                id: "settings.organizationDetails.mileageTracking.visibility.all",
                defaultMessage: "All Users",
                description:
                  "Settings organization details - MileageTracking - Visibility - All users",
              })}
            </Radio>
          </Space>
        </RadioGroup>
      </Form.Item>
    </Form>
  ) : (
    <Upsell />
  );
};

const mapStateToProps = (state: RootState) => ({
  isMileageTrackingVisible: isMileageTrackingVisible(state),
  organizationId: getOrganizationId(state),
  organizationSettings: getOrganizationSettings(state) ?? [],
});

const mapDispatchToProps = {
  onUpdateOrganization: updateOrganization.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(MileageTracking);
